import React, { useState } from "react";
import StaySearchbox from "./../../search/StaySearchbox";
import { useTranslation, Trans } from "react-i18next";

//scss
import "../../../scss/common.scss";
import "../../../scss/component.scss";

import {HotelAllSearchResponse, selectHotelDetail} from "../../../types/Hotel";
import { PlanSelectFormData } from "../../../types/planSelectForm";
import SectionRetry from "../../SectionRetry";

type Props = {
  canSelect: boolean;
  isLoading: boolean;
  isListLoading: boolean;
  /**
   * ホテルの**選択だけ**を止めるか（2026-09-07 再レビュー）。
   * ★基本プラン／フライト区画全体の読み込み中に立てる。ホテルを選び切ると次のページへ
   *   進めるため、古いプラン・便のまま確定されるのを防ぐ。
   * ★`isLoading`（ホテル自身の読み込み）とは分ける。まとめてしまうと、
   *   **ホテルの再読み込みボタンまで押せなくなる**。
   */
  selectDisabled?: boolean;
  planSelectFormData: PlanSelectFormData;
  selectPlan(planSelectFormData: PlanSelectFormData): void;
  callback: () => void;
  sort: (type: string, val: number) => void;
  hotelAllData: HotelAllSearchResponse | undefined;
  hotelController: AbortController;
  handleHotelSelected: (selectedHotel: selectHotelDetail) => void;
  /** ホテル一覧が上流障害で取れなかったとき（issue #2992 / 報告書 §8 No.4） */
  hotelError?: {retryAfterSec: number};
  /** ホテル区画だけを再要求する（ページ全体を再読み込みしない） */
  onRetryHotel?: () => void;
};

const SectionStay: React.FC<Props> = ({
  canSelect,
  isLoading,
  isListLoading,
  selectDisabled,
  planSelectFormData,
  selectPlan,
  callback,
  sort,
  hotelAllData,
  hotelController,
  handleHotelSelected,
  hotelError,
  onRetryHotel
}) => {
  const { t, i18n } = useTranslation();
  const selectHotelPlan = (selectData: PlanSelectFormData) => {
    selectPlan(selectData);
  };
  const [hotelSelectedStep, setHotelSelectedStep] = useState<number>(1); // 1=宿泊施設、2=宿泊プラン

  return (
    <section id="section-hotel">
      <div className="wrap-content-900">
        <h2 className="heading-2 mb-24-md">{t("宿泊の選択")}</h2>

        {canSelect === false ? (
          <div className="box-white large">
            <p className="c-light_gray fw-b">
              {t("基本プランの日程を設定後に選択いただけます。")}
            </p>
          </div>
        ) : hotelError && !(hotelAllData?.hotelList && hotelAllData.hotelList.length > 0) ? (
          /*
           * ★上流障害で一覧が1件も取れていない場合。
           *   ここで StaySearchbox を描画すると「検索結果なし」(SearchNotFound) が誤表示され、
           *   利用者は条件を変えて検索し直し、そのぶん上流コールが増える
           *   （2026-08-31 実測・issue #2992 / 報告書 §8 No.4）。
           */
          <SectionRetry
            retryAfterSec={hotelError.retryAfterSec}
            isLoading={isLoading}
            onRetry={() => onRetryHotel?.()}
            message={t("宿泊施設を取得できませんでした。") ?? undefined}
          />
        ) : (
          <>
            {/* 追加読み込み（もっと見る）の失敗。取得済みの一覧は残したまま案内する */}
            {hotelError && (
              <SectionRetry
                retryAfterSec={hotelError.retryAfterSec}
                isLoading={isListLoading}
                onRetry={() => onRetryHotel?.()}
                message={t("宿泊施設を取得できませんでした。") ?? undefined}
              />
            )}
            {hotelAllData?.success && (
              <p className="fz-14 fz-16-md fw-b mb-16 mb-20-md">
                {hotelSelectedStep === 1
                  ? t("ご希望の宿泊施設をお選びください")
                  : t("ご希望の宿泊プランをお選びください")}
              </p>
            )}
            <StaySearchbox
              planSelectFormData={planSelectFormData}
              // ★選択の抑止はこちらに寄せる（再読み込みボタンは `isLoading` で判断する）
              isLoading={isLoading || !!selectDisabled}
              isListLoading={isListLoading}
              selectPlanSelectFormData={selectHotelPlan}
              callback={callback}
              setHotelSelectedStep={setHotelSelectedStep}
              sort={sort}
              hotelAllData={hotelAllData}
              hotelController={hotelController}
              handleHotelSelected={handleHotelSelected}
            ></StaySearchbox>
          </>
        )}
      </div>
    </section>
  );
};

export default SectionStay;
